import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Package, Phone, Search, Clock, CheckCircle, XCircle, AlertCircle, CalendarDays, QrCode } from 'lucide-react'
import QRCode from 'qrcode'
import { customerApi, formatItemName } from '../services/api'
import { useSession } from '../contexts/SessionContext'
import { useRestaurant } from '../contexts/RestaurantContext'
import { getRecentOrders, getRecentReservations, getQROrders, type RecentOrderSnapshot, type RecentReservationSnapshot, type QROrderSnapshot } from '../utils/recentActivity'

interface OrderItem {
  id: string
  item_name: string
  quantity: number
  unit_price: number
  total_price: number
  status: string
}

interface Order {
  id: string
  order_number: string
  customer_phone?: string
  customer_name?: string
  order_type: string
  status: string
  subtotal: number
  tax_amount: number
  service_charge: number
  total_amount: number
  payment_status: string
  order_time: string
  created_at: string
  order_items: OrderItem[]
}

/** Order from API (full), device snapshot (summary), or QR order (pending scan, full details). */
type DisplayOrder =
  | Order
  | (RecentOrderSnapshot & { id?: string; order_items?: OrderItem[]; subtotal?: number; tax_amount?: number; service_charge?: number; order_time?: string })
  | (QROrderSnapshot & { order_number?: string; order_time?: string; total_amount?: number })

export default function MyOrders() {
  const navigate = useNavigate()
  const location = useLocation()
  const { session, isSessionValid, createSession } = useSession()
  const { formatPrice, taxRate = 0, serviceChargeRate = 0 } = useRestaurant()
  const [orders, setOrders] = useState<Order[]>([])
  const [recentOrders, setRecentOrders] = useState<RecentOrderSnapshot[]>([])
  const [qrOrders, setQrOrders] = useState<QROrderSnapshot[]>([])
  const [recentReservations, setRecentReservations] = useState<RecentReservationSnapshot[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPhoneVerification, setShowPhoneVerification] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [orderNumber, setOrderNumber] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [qrModalOrder, setQrModalOrder] = useState<QROrderSnapshot | null>(null)
  const [qrModalDataUrl, setQrModalDataUrl] = useState<string | null>(null)

  // Load device-stored recent orders, QR orders, and reservations on mount and when navigating to this page
  useEffect(() => {
    setRecentOrders(getRecentOrders())
    setQrOrders(getQROrders())
    setRecentReservations(getRecentReservations())
  }, [location.pathname])

  // Also load once on mount so we never show stale empty list (e.g. after placing order and navigating here)
  useEffect(() => {
    setRecentOrders(getRecentOrders())
    setQrOrders(getQROrders())
    setRecentReservations(getRecentReservations())
  }, [])

  useEffect(() => {
    if (isSessionValid() && session) {
      loadOrders()
    }
  }, [session, isSessionValid])

  const loadOrders = async () => {
    if (!session) return
    
    setLoading(true)
    setError(null)
    try {
      const response = await customerApi.getMyOrders(session.accessToken)
      if (response.success && response.data && Array.isArray(response.data)) {
        setOrders(response.data as Order[])
      } else {
        setError(response.error || 'Failed to load orders')
      }
    } catch (err) {
      setError('Failed to load orders. Please try again.')
      console.error('Error loading orders:', err)
    } finally {
      setLoading(false)
    }
  }

  const handlePhoneVerification = async (e: React.FormEvent) => {
    e.preventDefault()
    setVerifying(true)
    setError(null)

    if (!phoneNumber.trim() || !orderNumber.trim()) {
      setError('Please enter both phone number and order number')
      setVerifying(false)
      return
    }

    try {
      const response = await customerApi.verifyOrderAccess(phoneNumber.trim(), orderNumber.trim())
      if (response.success && response.data) {
        const verifyData = response.data as { phoneNumber?: string; accessToken: string }
        // Create session with access token
        createSession(verifyData.phoneNumber || phoneNumber.trim(), verifyData.accessToken)
        // Wait a bit for session to be set, then load orders
        setTimeout(async () => {
          await loadOrders()
          setShowPhoneVerification(false)
        }, 100)
      } else {
        setError(response.error || 'Invalid phone number or order number. Please check and try again.')
      }
    } catch (err) {
      setError('Failed to verify. Please try again.')
      console.error('Error verifying order access:', err)
    } finally {
      setVerifying(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
      case 'pending_scan':
        return 'bg-yellow-100 text-yellow-800'
      case 'confirmed':
        return 'bg-amber-100 text-amber-800'
      case 'preparing':
        return 'bg-orange-100 text-orange-800'
      case 'ready':
        return 'bg-green-100 text-green-800'
      case 'completed':
        return 'bg-gray-100 text-gray-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: string) => {
    if (status === 'pending_scan') return 'Pending (show QR to waiter)'
    return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ')
  }

  const handleShowQROrder = (order: QROrderSnapshot) => {
    if (!order.qrPayload) return
    setQrModalOrder(order)
    setQrModalDataUrl(null)
    QRCode.toDataURL(JSON.stringify(order.qrPayload), { width: 280, margin: 2 })
      .then((dataUrl) => setQrModalDataUrl(dataUrl))
      .catch(() => setQrModalOrder(null))
  }

  const closeQrModal = () => {
    setQrModalOrder(null)
    setQrModalDataUrl(null)
  }

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />
      case 'cancelled':
        return <XCircle className="w-4 h-4" />
      case 'pending':
        return <Clock className="w-4 h-4" />
      default:
        return <AlertCircle className="w-4 h-4" />
    }
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const mergedOrders: DisplayOrder[] = useMemo(() => {
    const byKey = new Map<string, DisplayOrder>()
    for (const s of recentOrders) {
      byKey.set(s.order_number, {
        ...s,
        id: s.order_number,
        order_time: s.created_at
      })
    }
    for (const o of orders) {
      const key = o.order_number ?? o.id
      if (key) byKey.set(String(key), o)
    }
    const apiOrderTimesByTotal = new Map<number, number>()
    for (const o of orders) {
      const t = o.total_amount ?? 0
      const orderTime = (o.order_time || (o as any).created_at || '').toString()
      const ms = orderTime ? new Date(orderTime).getTime() : 0
      if (ms && (!apiOrderTimesByTotal.has(t) || apiOrderTimesByTotal.get(t)! < ms)) {
        apiOrderTimesByTotal.set(t, ms)
      }
    }
    const MATCH_MS = 2 * 60 * 1000
    for (const q of qrOrders) {
      const qTime = new Date(q.created_at).getTime()
      const apiTime = apiOrderTimesByTotal.get(q.total)
      if (apiTime != null && Math.abs(apiTime - qTime) < MATCH_MS) continue
      byKey.set(q.id, {
        ...q,
        order_number: undefined,
        order_time: q.created_at,
        total_amount: q.total
      })
    }
    const list = Array.from(byKey.values())
    list.sort((a, b) => {
      const dateA = (a.order_time || (a as any).created_at || '').toString()
      const dateB = (b.order_time || (b as any).created_at || '').toString()
      return dateB.localeCompare(dateA)
    })
    return list
  }, [orders, recentOrders, qrOrders])

  const hasFullOrder = (o: DisplayOrder): o is Order => 'order_items' in o && Array.isArray((o as Order).order_items)
  const isQROrder = (o: DisplayOrder): o is QROrderSnapshot => (o as QROrderSnapshot).status === 'pending_scan'

  if (showPhoneVerification) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-700 hover:text-amber-500"
            >
              <ArrowLeft className="w-5 h-5 rtl:scale-x-[-1]" />
              <span>Back</span>
            </button>
          </div>
        </header>

        <div className="max-w-md mx-auto px-4 py-12">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-6">
              <Package className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">View My Orders</h1>
              <p className="text-gray-600">
                Enter your phone number and order number to view your orders
              </p>
            </div>

            <form onSubmit={handlePhoneVerification} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+1234567890"
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Package className="w-4 h-4 inline mr-1" />
                  Order Number
                </label>
                <input
                  type="text"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
                  placeholder="ORD-20250109-1234"
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  You can find this on your order confirmation page
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={verifying}
                className="w-full bg-amber-500 text-white py-3 rounded-lg font-semibold hover:bg-amber-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {verifying ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    View Orders
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <button
                type="button"
                onClick={() => setShowPhoneVerification(false)}
                className="block w-full text-amber-500 hover:text-amber-600 text-sm"
              >
                Back to device orders
              </button>
              <Link
                to="/"
                className="block text-amber-500 hover:text-blue-700 text-sm"
              >
                Back to Menu
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-700 hover:text-amber-500"
            >
              <ArrowLeft className="w-5 h-5 rtl:scale-x-[-1]" />
              <span>Back</span>
            </button>
            <h1 className="text-2xl font-bold text-gray-900">My Orders</h1>
            <div className="w-20"></div> {/* Spacer for centering */}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading && mergedOrders.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your orders...</p>
          </div>
        ) : error && mergedOrders.length === 0 ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        ) : null}

        {/* Orders section */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Orders</h2>
          {mergedOrders.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-6 text-center">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">No orders yet.</p>
              <Link to="/" className="text-amber-500 hover:text-amber-600 text-sm font-medium mt-2 inline-block">
                Browse Menu
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {mergedOrders.map((order) => (
                <div key={(order as any).id ?? order.order_number ?? (order as any).created_at} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {isQROrder(order)
                          ? 'Order'
                          : `Order #${order.order_number}`}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {formatDateTime((order.order_time || (order as any).created_at) ?? '')}
                      </p>
                    </div>
                    {isQROrder(order) && (order as QROrderSnapshot).qrPayload ? (
                      <button
                        type="button"
                        onClick={() => handleShowQROrder(order)}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
                        title="Show QR code"
                        aria-label="Show QR code"
                      >
                        <QrCode className="w-6 h-6" />
                      </button>
                    ) : isQROrder(order) ? (
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        <span>{getStatusLabel(order.status)}</span>
                      </span>
                    ) : (
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {getStatusIcon(order.status)}
                        <span>{getStatusLabel(order.status)}</span>
                      </span>
                    )}
                  </div>

                  {/* Items list above order summary */}
                  {hasFullOrder(order) && order.order_items && order.order_items.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Items:</h4>
                      <div className="space-y-2">
                        {order.order_items.map((item) => (
                          <div key={item.id} className="flex justify-between text-sm">
                            <span className="text-gray-700">
                              {formatItemName(item)} × {item.quantity}
                            </span>
                            <span className="text-gray-900">{formatPrice(item.total_price)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {isQROrder(order) && order.items && order.items.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Items:</h4>
                      <div className="space-y-2">
                        {order.items.map((item, idx) => (
                          <div key={`${order.id}-${idx}`} className="flex justify-between text-sm">
                            <span className="text-gray-700">
                              {item.name} × {item.quantity}
                            </span>
                            <span className="text-gray-900">{formatPrice(item.total_price)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 border-t pt-4">
                    {(hasFullOrder(order) || isQROrder(order)) && (hasFullOrder(order) ? order.subtotal : (order as QROrderSnapshot).subtotal) != null && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Subtotal:</span>
                        <span className="text-gray-900">{formatPrice(hasFullOrder(order) ? order.subtotal! : (order as QROrderSnapshot).subtotal)}</span>
                      </div>
                    )}
                    {hasFullOrder(order) && order.tax_amount != null && order.tax_amount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">VAT ({taxRate}%):</span>
                        <span className="text-gray-900">{formatPrice(order.tax_amount)}</span>
                      </div>
                    )}
                    {isQROrder(order) && taxRate > 0 && (order as QROrderSnapshot).subtotal != null && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">VAT ({taxRate}%):</span>
                        <span className="text-gray-900">{formatPrice(((order as QROrderSnapshot).subtotal * taxRate) / 100)}</span>
                      </div>
                    )}
                    {hasFullOrder(order) && order.service_charge != null && order.service_charge > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Service charge ({serviceChargeRate}%):</span>
                        <span className="text-gray-900">{formatPrice(order.service_charge)}</span>
                      </div>
                    )}
                    {isQROrder(order) && serviceChargeRate > 0 && (order as QROrderSnapshot).subtotal != null && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Service charge ({serviceChargeRate}%):</span>
                        <span className="text-gray-900">{formatPrice(((order as QROrderSnapshot).subtotal * serviceChargeRate) / 100)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-semibold border-t pt-2">
                      <span className="text-gray-900">Total:</span>
                      <span className="text-amber-500 text-lg">{formatPrice(order.total_amount ?? (order as QROrderSnapshot).total)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Reservations section */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Reservations</h2>
          {recentReservations.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-6 text-center">
              <CalendarDays className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">No reservations yet.</p>
              <Link to="/reservation" className="text-amber-500 hover:text-amber-600 text-sm font-medium mt-2 inline-block">
                Make a reservation
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {recentReservations.map((res) => (
                <div key={res.id} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{res.customer_name}</h3>
                      <p className="text-sm text-gray-500">{formatDateTime(res.reservation_time)}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Booked
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Party of {res.party_size}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* QR code modal – blurred background */}
      {qrModalOrder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={closeQrModal}
          role="dialog"
          aria-modal="true"
          aria-label="QR code"
        >
          <div
            className="bg-white rounded-2xl shadow-xl p-6 max-w-sm mx-4 flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end w-full mb-2">
              <button
                type="button"
                onClick={closeQrModal}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Show this QR to the waiter</p>
            {qrModalDataUrl ? (
              <img
                src={qrModalDataUrl}
                alt="Order QR code"
                className="w-64 h-64 rounded-lg object-contain"
              />
            ) : (
              <div className="w-64 h-64 flex items-center justify-center rounded-lg bg-gray-100">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-amber-500 border-t-transparent" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

